import { useCallback, useMemo } from "react"
import { useIntl } from "react-intl"
import { useNavigate } from "react-router-dom"

import { AssignedConversation } from "@doktor-se/bones-ui/dist/web-shared/types"
import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { clearRejectedReassignment } from "api"
import { useAppDispatch, useAppSelector } from "lib/hooks"
import { personalBookingsSelectors } from "reducers/booking"
import { setConversation } from "reducers/selected"

import { BooleanCell, DateCell, NameCell } from "components/v2/conversationList/Cells"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

interface MyBookingsListProps {
  title: string
  conversations: AssignedConversation[]
}

interface MyBookingsListItem extends ConversationListItem<AssignedConversation> {}

const MyBookingsList = ({ title, conversations }: MyBookingsListProps) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const intl = useIntl()
  const bookings = useAppSelector(personalBookingsSelectors.selectAll).filter(b => b.status === "booked")
  const kinds = useAppSelector(state => state.booking.bookingTypes)
  const appLanguage = useAppSelector(state => state.app.language)
  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "kind",
      name: intl.formatMessage({ id: "label.overview.booking_type" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.booked_time" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const mapConversationListItem = useCallback(
    (conv: AssignedConversation): MyBookingsListItem => {
      const isChildConversation = conv.profile?.type === "child"
      const booking = bookings.find(b => b.id === conv.metadata?.bookingId)
      if (!booking) return {} as MyBookingsListItem

      return {
        conversation: conv,
        values: {
          pnr: isChildConversation
            ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
            : formatPnr(conv.account?.pnr, conv.metadata?.region),
          name: (
            <NameCell
              name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
              isChild={isChildConversation}
            />
          ),
          kind: kinds.find(k => k.id === booking.kindId)?.localisedNames[appLanguage],
          start: <DateCell value={booking?.start} />,
          listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
          recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
          medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
        },
        sortValues: {
          ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent"], {}),
          kind: sortValueFunctions.assignedBookingType(conv, { bookingTypes: kinds, appLanguage, bookings }),
          start: booking?.start || ""
        }
      }
    },
    [appLanguage, bookings, kinds]
  )

  const list = useMemo(
    () => conversations.map(conv => mapConversationListItem(conv)).filter(l => l.conversation),
    [conversations, mapConversationListItem]
  )

  const onConversationClick = (item: MyBookingsListItem) => {
    const { id, isReassignmentRejected } = item.conversation
    if (isReassignmentRejected) {
      dispatch(clearRejectedReassignment(id))
    }
    dispatch(setConversation(id))
    navigate("/")
  }

  const shouldNotifyItem = (item: MyBookingsListItem) => {
    return !!item.conversation.isReassignmentRejected
  }

  return (
    <ConversationTable
      title={title}
      columns={columns}
      items={list}
      emptyMessage={intl.formatMessage({ id: "label.overview.my_bookings_empty" })}
      onItemClick={onConversationClick}
      shouldNotify={shouldNotifyItem}
    />
  )
}

export default MyBookingsList
